import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target, Trophy, Flame, Clock, CheckCircle, XCircle,
  ArrowRight, Gift, Star, Zap, Calendar, Award
} from 'lucide-react';
import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface Question {
  id: number;
  type: string;
  difficulty: number;
  questionText: string;
  options: string;
  correctAnswer: string;
  explanation: string;
  topicName: string;
}

interface Challenge {
  id: number;
  date: string;
  rewardPoints: number;
  isCompleted: boolean;
  completedAt?: number;
  questions: Question[];
  totalQuestions: number;
}

interface ChallengeResult {
  correctCount: number;
  totalQuestions: number;
  accuracy: number;
  earnedPoints: number;
  bonusMultiplier: number;
  results: Array<{
    questionId: number;
    isCorrect: boolean;
    correctAnswer: string;
    explanation: string;
  }>;
}

export function DailyChallengePage() {
  const { user } = useAuthStore();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ChallengeResult | null>(null);
  const [history, setHistory] = useState<any>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (user) {
      loadChallenge();
      loadHistory();
    }
  }, [user]);

  useEffect(() => {
    if (challenge && !challenge.isCompleted && !result && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            handleSubmit();
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [challenge, result, timeLeft]);

  async function loadChallenge() {
    setLoading(true);
    try {
      const res = await client.api.fetch('/api/challenge/today');
      const data = await res.json();
      setChallenge(data.data);
    } catch (error) {
      console.error('Failed to load challenge:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    try {
      const res = await client.api.fetch('/api/challenge/history?limit=30');
      const data = await res.json();
      setHistory(data.data);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  }

  function handleAnswer(questionId: number, answer: string) {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  }

  async function handleSubmit() {
    if (!challenge || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const answerList = challenge.questions.map(q => ({
        questionId: q.id,
        answer: answers[q.id] || '',
        timeSpent: Math.round((600 - timeLeft) / challenge.questions.length),
      }));

      const res = await client.api.fetch('/api/challenge/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challengeId: challenge.id,
          answers: answerList,
        }),
      });

      const data = await res.json();
      if (data.data) {
        setResult(data.data);
        loadHistory();
      }
    } catch (error) {
      console.error('Failed to submit:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  function formatTime(seconds: number) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">暂无挑战</h2>
          <p className="text-slate-600">请稍后再来</p>
        </div>
      </div>
    );
  }

  // Show result screen
  if (result || challenge.isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-3xl shadow-xl p-8 text-center"
          >
            {result ? (
              <>
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Trophy className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">挑战完成！</h1>
                <p className="text-slate-600 mb-8">太棒了，你完成了今天的挑战</p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-emerald-50 rounded-2xl p-4">
                    <div className="text-3xl font-bold text-emerald-600">{result.correctCount}/{result.totalQuestions}</div>
                    <div className="text-sm text-emerald-700">正确答题</div>
                  </div>
                  <div className="bg-blue-50 rounded-2xl p-4">
                    <div className="text-3xl font-bold text-blue-600">{result.accuracy}%</div>
                    <div className="text-sm text-blue-700">准确率</div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-6 mb-8">
                  <div className="flex items-center justify-center gap-2 text-white mb-2">
                    <Gift className="w-5 h-5" />
                    <span className="text-lg">获得奖励</span>
                  </div>
                  <div className="text-4xl font-bold text-white">+{result.earnedPoints}</div>
                  <div className="text-white/80 text-sm mt-1">
                    {result.bonusMultiplier > 1 && `(${result.bonusMultiplier}x 准确率加成)`}
                  </div>
                </div>

                {/* Answer details */}
                <div className="text-left space-y-4 mb-8">
                  <h3 className="font-semibold text-slate-800">答题详情</h3>
                  {result.results.map((r, idx) => {
                    const question = challenge.questions.find(q => q.id === r.questionId);
                    return (
                      <div key={r.questionId} className={`p-4 rounded-xl ${r.isCorrect ? 'bg-emerald-50' : 'bg-red-50'}`}>
                        <div className="flex items-start gap-3">
                          {r.isCorrect ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <div className="text-sm text-slate-600 mb-1">题目 {idx + 1}</div>
                            <div className="text-slate-800 text-sm">{question?.questionText?.slice(0, 100)}...</div>
                            {!r.isCorrect && (
                              <div className="mt-2 text-sm">
                                <span className="text-red-600">正确答案：{r.correctAnswer}</span>
                              </div>
                            )}
                            {r.explanation && (
                              <div className="mt-2 text-xs text-slate-500 bg-white/50 p-2 rounded">
                                {r.explanation}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">今日已完成</h1>
                <p className="text-slate-600 mb-8">你已经完成了今天的挑战，明天再来吧！</p>
              </>
            )}

            {/* History section */}
            {history && (
              <div className="border-t pt-6">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center justify-center gap-2 text-slate-600 hover:text-slate-800 mx-auto"
                >
                  <Calendar className="w-4 h-4" />
                  <span>{showHistory ? '收起历史' : '查看历史'}</span>
                </button>

                <AnimatePresence>
                  {showHistory && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-4 overflow-hidden"
                    >
                      <div className="flex items-center justify-center gap-6 mb-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-orange-600">{history.currentStreak}</div>
                          <div className="text-xs text-slate-500">连续天数</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{history.totalCompleted}</div>
                          <div className="text-xs text-slate-500">总完成数</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {Array.from({ length: 28 }).map((_, i) => {
                          const date = new Date();
                          date.setDate(date.getDate() - (27 - i));
                          const dateStr = date.toISOString().split('T')[0];
                          const completed = history.completions?.some((c: any) => c.date === dateStr);
                          return (
                            <div
                              key={i}
                              className={`w-full aspect-square rounded-sm ${
                                completed ? 'bg-orange-400' : 'bg-slate-100'
                              }`}
                              title={dateStr}
                            />
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-3 rounded-full font-semibold mt-6 hover:shadow-lg transition-shadow"
            >
              返回首页
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show challenge questions
  const currentQuestion = challenge.questions[currentIndex];
  const options = currentQuestion?.options ? JSON.parse(currentQuestion.options) : [];
  const progress = Object.keys(answers).length / challenge.questions.length * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="w-6 h-6 text-orange-500" />
              每日挑战
            </h1>
            <p className="text-slate-600 text-sm">{challenge.date}</p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            timeLeft < 60 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-700'
          }`}>
            <Clock className="w-4 h-4" />
            <span className="font-mono font-semibold">{formatTime(timeLeft)}</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-white rounded-full h-3 mb-6 overflow-hidden shadow-inner">
          <motion.div
            className="h-full bg-gradient-to-r from-orange-400 to-amber-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
          />
        </div>

        {/* Reward info */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-4 mb-6 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <Gift className="w-8 h-8" />
            <div>
              <div className="font-semibold">完成奖励</div>
              <div className="text-sm text-white/80">全对可获得双倍积分!</div>
            </div>
          </div>
          <div className="text-2xl font-bold">+{challenge.rewardPoints}</div>
        </div>

        {/* Question card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -50, opacity: 0 }}
            className="bg-white rounded-3xl shadow-lg p-6 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-500">
                第 {currentIndex + 1} / {challenge.questions.length} 题
              </span>
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                {currentQuestion?.topicName}
              </span>
            </div>

            <div className="text-lg text-slate-800 mb-6 leading-relaxed">
              {currentQuestion?.questionText}
            </div>

            {/* Options */}
            <div className="space-y-3">
              {options.map((option: string, idx: number) => {
                const letter = String.fromCharCode(65 + idx);
                const isSelected = answers[currentQuestion.id] === letter;
                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(currentQuestion.id, letter)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-slate-200 hover:border-orange-300 hover:bg-orange-50/50'
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                      isSelected ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {letter}
                    </span>
                    {option}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            className="px-6 py-3 rounded-full bg-white shadow text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md transition-shadow"
          >
            上一题
          </button>

          {/* Question dots */}
          <div className="flex gap-2">
            {challenge.questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentIndex
                    ? 'bg-orange-500 w-6'
                    : answers[q.id]
                    ? 'bg-emerald-400'
                    : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          {currentIndex === challenge.questions.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || Object.keys(answers).length < challenge.questions.length}
              className="px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-shadow flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  提交中...
                </>
              ) : (
                <>
                  提交答案
                  <Zap className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => setCurrentIndex(Math.min(challenge.questions.length - 1, currentIndex + 1))}
              className="px-6 py-3 rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors flex items-center gap-2"
            >
              下一题
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
